import { useState, useRef, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { UploadSimple, ArrowRight, CheckCircle, Table as TableIcon } from "@phosphor-icons/react";

const NONE = "__none__";

const TARGET_FIELDS = [
  { key: "name", label: "Firmenname", required: true, syn: ["name", "firma", "firmenname", "company"] },
  { key: "name_for_emails", label: "Markenname (name_for_emails)", syn: ["name_for_emails", "marke", "markenname"] },
  { key: "email", label: "E-Mail", syn: ["email", "e-mail", "mail", "e_mail", "emailadresse", "e-mail-adresse"] },
  { key: "source_label", label: "Quelle (Source)", syn: ["quelle", "source", "herkunft"] },
  { key: "street", label: "Straße + Nr.", syn: ["street", "straße", "strasse", "adresse", "strasse_nr"] },
  { key: "plz", label: "PLZ", syn: ["plz", "postal_code", "postleitzahl", "zip"] },
  { key: "city", label: "Stadt", syn: ["city", "stadt", "ort"] },
  { key: "firmentyp", label: "Firmentyp", syn: ["firmentyp", "rechtsform", "typ"] },
  { key: "gruendungsdatum", label: "Gründungsdatum", syn: ["gruendungsdatum", "gründungsdatum", "datum"] },
  { key: "beschreibung", label: "Beschreibung", syn: ["beschreibung", "zweck", "description"] },
  { key: "tags", label: "Tags (kommagetrennt)", syn: ["tags", "branchen", "keywords"] },
  { key: "geschaeftsfuehrung", label: "Geschäftsführung", syn: ["geschaeftsfuehrung", "geschäftsführung", "gf"] },
  { key: "hrb", label: "HRB", syn: ["hrb", "handelsregister"] },
  { key: "euid", label: "EUID", syn: ["euid"] },
];

function parseCSV(text) {
  text = text.replace(/^\uFEFF/, "");
  const firstLine = text.split(/\r?\n/)[0] || "";
  const delim = firstLine.split(";").length > firstLine.split(",").length ? ";" : ",";
  const rows = [];
  let field = "", row = [], inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else inQuotes = false;
      } else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === delim) { row.push(field); field = ""; }
    else if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; }
    else if (c === "\r") { /* skip */ }
    else field += c;
  }
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  const headers = (rows.shift() || []).map((h) => h.trim());
  const data = rows
    .filter((r) => r.some((c) => (c || "").trim() !== ""))
    .map((r) => {
      const obj = {};
      headers.forEach((h, idx) => (obj[h] = (r[idx] ?? "").trim()));
      return obj;
    });
  return { headers, data };
}

export default function ManuellerUpload() {
  const fileRef = useRef();
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [defaultSource, setDefaultSource] = useState("Manueller Upload");

  const onFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setResult(null);
    const text = await file.text();
    const { headers: h, data } = parseCSV(text);
    if (h.length === 0 || data.length === 0) {
      toast.error("Keine Daten in der CSV gefunden");
      return;
    }
    setHeaders(h);
    setRows(data);
    // auto-map
    const auto = {};
    TARGET_FIELDS.forEach((f) => {
      const found = h.find((col) => f.syn.includes(col.toLowerCase().trim()));
      auto[f.key] = found || NONE;
    });
    setMapping(auto);
    toast.success(`${data.length} Zeilen geladen`);
  };

  const setMap = (key, val) => setMapping((m) => ({ ...m, [key]: val }));

  const mappedLeads = useMemo(() => {
    if (!mapping.name || mapping.name === NONE) return [];
    return rows
      .map((r) => {
        const obj = {};
        Object.entries(mapping).forEach(([field, header]) => {
          if (header && header !== NONE) obj[field] = r[header];
        });
        return obj;
      })
      .filter((o) => (o.name || "").trim());
  }, [rows, mapping]);

  const doImport = async () => {
    if (mappedLeads.length === 0) return toast.error("Bitte mindestens den Firmennamen zuordnen");
    setImporting(true);
    try {
      const { data } = await api.post("/leads/import", {
        leads: mappedLeads,
        source_label: defaultSource.trim() || "Manueller Upload",
      });
      setResult(data);
      toast.success(`${data.new} neue Leads importiert, ${data.duplicates} Duplikate übersprungen`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import fehlgeschlagen");
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setHeaders([]); setRows([]); setMapping({}); setResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="font-heading text-4xl font-bold tracking-tight">Manueller Upload</h1>
        <p className="text-sm text-zinc-500 mt-1">
          CSV hochladen, Spalten zuordnen und als Leads übernehmen. Duplikate werden automatisch erkannt.
        </p>
      </div>

      <div className="bg-white border border-zinc-200 rounded-md p-6 mb-6">
        <Label className="text-xs uppercase tracking-widest text-zinc-500">CSV-Datei</Label>
        <div className="flex gap-2 mt-1.5">
          <Input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} data-testid="csv-file-input" />
          {headers.length > 0 && (
            <Button variant="outline" onClick={reset} className="rounded-sm border-zinc-300 shrink-0">Zurücksetzen</Button>
          )}
        </div>
        <p className="text-xs text-zinc-400 mt-2">Trennzeichen (Komma/Semikolon) wird automatisch erkannt.</p>
      </div>

      {headers.length > 0 && (
        <>
          <div className="bg-white border border-zinc-200 rounded-md p-6 mb-6" data-testid="mapping-panel">
            <h2 className="font-heading text-lg font-semibold mb-1">Spalten zuordnen</h2>
            <p className="text-sm text-zinc-500 mb-4">{rows.length} Zeilen erkannt. Ordne die CSV-Spalten den Lead-Feldern zu.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {TARGET_FIELDS.map((f) => (
                <div key={f.key}>
                  <Label className="text-xs text-zinc-600 flex items-center gap-1">
                    {f.label}
                    {f.required && <span className="text-[#E53935]">*</span>}
                  </Label>
                  <Select value={mapping[f.key] || NONE} onValueChange={(v) => setMap(f.key, v)}>
                    <SelectTrigger className="mt-1 h-9 rounded-sm" data-testid={`map-${f.key}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>— nicht zuordnen —</SelectItem>
                      {headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <div className="mt-6 max-w-md">
              <Label className="text-xs text-zinc-600">Standard-Quelle</Label>
              <Input value={defaultSource} onChange={(e) => setDefaultSource(e.target.value)} className="mt-1 h-9" data-testid="default-source-input" />
              <p className="text-xs text-zinc-400 mt-1">
                Wird als „Quelle" gespeichert, wenn keine Spalte zugeordnet ist oder die Zelle leer ist (z.B. „Gekauft").
              </p>
            </div>
            <div className="flex items-center gap-4 mt-6">
              <Button onClick={doImport} disabled={importing || mappedLeads.length === 0} data-testid="import-button" className="bg-[#002FA7] hover:bg-[#001D6E] text-white rounded-sm h-11 px-6">
                <UploadSimple size={16} className="mr-2" />
                {importing ? "Importiere…" : `${mappedLeads.length} Leads importieren`}
              </Button>
              {result && (
                <span className="text-sm text-[#00796B] flex items-center gap-1" data-testid="import-result">
                  <CheckCircle size={16} weight="fill" /> {result.new} neu · {result.duplicates} Duplikate · {result.skipped} ohne Namen
                </span>
              )}
            </div>
          </div>

          {/* Preview */}
          <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
            <div className="px-4 py-3 text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-1 border-b border-zinc-100">
              <TableIcon size={14} /> Vorschau (erste 5 Zeilen)
            </div>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
                  {TARGET_FIELDS.filter((f) => mapping[f.key] && mapping[f.key] !== NONE).map((f) => (
                    <th key={f.key} className="px-3 py-2 font-medium whitespace-nowrap">{f.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {mappedLeads.slice(0, 5).map((l, i) => (
                  <tr key={i} className="border-t border-zinc-100">
                    {TARGET_FIELDS.filter((f) => mapping[f.key] && mapping[f.key] !== NONE).map((f) => (
                      <td key={f.key} className="px-3 py-2 text-zinc-600 max-w-[220px] truncate">{l[f.key]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
